import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ErrorText, Field, Page, Submit, passwordProblem } from '../components';
import { useAction, useDevice, useSnapshot } from '../state';

export function Unlock() {
  const device = useDevice();
  const { notice } = useSnapshot();
  const { busy, error, setError, run } = useAction();
  const forget = useAction();
  const [password, setPassword] = useState('');
  const [confirmForget, setConfirmForget] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!password) return setError('Enter your password.');
    void run(() => device.unlock(password));
  };

  return (
    <Page>
      <h1>Unlock Curentia</h1>
      <p className="muted">Your records on this device are encrypted. Enter your password to open them.</p>
      {notice ? <p className="card warning">{notice}</p> : null}
      <form onSubmit={submit} noValidate>
        <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
        <ErrorText error={error} />
        <Submit busy={busy} busyText="Unlocking…">
          Unlock
        </Submit>
      </form>
      <div className="links">
        <Link to="/recover">Forgot your password? Use your recovery code</Link>
        {confirmForget ? (
          <div className="card danger">
            <p>This deletes this account's data from this device. Without a backup file it cannot be brought back.</p>
            <ErrorText error={forget.error} />
            <div className="stack">
              <button type="button" className="button danger" onClick={() => void forget.run(() => device.forgetDevice())}>
                Remove from this device
              </button>
              <button type="button" className="button secondary" onClick={() => setConfirmForget(false)}>
                Keep it
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="button secondary small" onClick={() => setConfirmForget(true)}>
            Use a different account
          </button>
        )}
      </div>
    </Page>
  );
}

export function Recover() {
  const device = useDevice();
  const { busy, error, setError, run } = useAction();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim()) return setError('Enter your recovery code.');
    const problem = passwordProblem(password, confirm);
    if (problem) return setError(problem);
    void run(() => device.recover(code, password));
  };

  return (
    <Page>
      <h1>Use your recovery code</h1>
      <p className="muted">The code you wrote down when you created your account opens your records. Then choose a new password. You need an internet connection for this.</p>
      <form onSubmit={submit} noValidate>
        <Field label="Recovery code" autoComplete="off" autoCapitalize="characters" value={code} onChange={(e) => setCode(e.target.value)} hint="24 letters and numbers, such as 7K2M-QX9D-…" />
        <Field label="New password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <Field label="Confirm new password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <ErrorText error={error} />
        <Submit busy={busy} busyText="Checking your code…">
          Reset password
        </Submit>
      </form>
      <div className="links">
        <Link to="/unlock">Back</Link>
      </div>
    </Page>
  );
}
